import is from '@sindresorhus/is';
import noop from 'lodash/noop';
import type { FunctionComponent, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { AnnouncementHistoryItem } from '~/shared/data-contract/announcements';

import { useActiveAnnouncement } from './api/use-active-announcement';
import { useAnnouncementHistory } from './api/use-announcement-history';
import { isKnownNotificationType } from './content';
import { useAnnouncementDismissal } from './use-announcement-dismissal';
import { WhatsNewModal } from './WhatsNewModal';

type AnnouncementsContextValue = {
    readonly history: readonly AnnouncementHistoryItem[] | undefined;
    readonly openModal: () => void;
};

const DISABLED_CONTEXT_VALUE: AnnouncementsContextValue = { history: undefined, openModal: noop };

const AnnouncementsContext = createContext<AnnouncementsContextValue | undefined>(undefined);

export const useAnnouncements = (): AnnouncementsContextValue => {
    const context = useContext(AnnouncementsContext);

    if (is.undefined(context)) {
        throw new Error('useAnnouncements must be used within AnnouncementsProvider');
    }

    return context;
};

export const AnnouncementsProvider: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => {
    const { applicationState } = useApplicationContext();
    const isUserProfileComplete = applicationState.myUserProfile.mapOr(false, (profile) => profile.exists === true);
    const isEnabled =
        isUserProfileComplete && applicationState.featureFlagService.isFeatureFlagEnabled('announcements-enabled');

    if (!isEnabled) {
        return <AnnouncementsContext.Provider value={DISABLED_CONTEXT_VALUE}>{children}</AnnouncementsContext.Provider>;
    }

    return <AnnouncementsProviderContent>{children}</AnnouncementsProviderContent>;
};

const AnnouncementsProviderContent: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => {
    const { data: activeAnnouncements } = useActiveAnnouncement();
    const { data: history } = useAnnouncementHistory();
    const { mutate: dismiss } = useAnnouncementDismissal();

    const [modalOpen, setModalOpen] = useState(false);

    const renderableActiveAnnouncements = useMemo(
        () => activeAnnouncements?.filter((a) => isKnownNotificationType(a.notificationType)),
        [activeAnnouncements],
    );
    const renderableHistory = useMemo(
        () => history?.filter((item) => isKnownNotificationType(item.notificationType)),
        [history],
    );

    const activeAnnouncement = renderableActiveAnnouncements?.[0];
    const latestHistoryItem = renderableHistory?.[0];
    const modalNotificationType = activeAnnouncement?.notificationType ?? latestHistoryItem?.notificationType;

    const activeAnnouncementId = activeAnnouncement?.id;

    useEffect(() => {
        if (is.undefined(activeAnnouncementId)) {
            return;
        }
        setModalOpen(true);
    }, [activeAnnouncementId]);

    const openModal = useCallback(() => {
        setModalOpen(true);
    }, []);

    const handleDismiss = useCallback(() => {
        if (is.undefined(activeAnnouncementId)) {
            return;
        }
        dismiss(activeAnnouncementId);
    }, [activeAnnouncementId, dismiss]);

    const value = useMemo<AnnouncementsContextValue>(
        () => ({ history: renderableHistory, openModal }),
        [renderableHistory, openModal],
    );

    return (
        <AnnouncementsContext.Provider value={value}>
            {children}
            {!is.undefined(modalNotificationType) && (
                <WhatsNewModal
                    notificationType={modalNotificationType}
                    open={modalOpen}
                    onDismiss={handleDismiss}
                    onOpenChange={setModalOpen}
                />
            )}
        </AnnouncementsContext.Provider>
    );
};
